import axios from 'axios'
import store from '@/store'
import { User } from '@/models/User'
import { ORDER_MENU_TYPE } from '@/utils/order-menu-type'
import { EventBus } from '@/utils/event-bus'

/**
 * API Client Helper Class
 *
 */
class APIClient {
  baseURL = `${process.env.VUE_APP_API_URL}/api/v1.0/`

  constructor () {
    this.initializeAxios()
  }

  initializeAxios () {
    this.axios = axios.create({
      baseURL: this.baseURL,
      headers: {
        Accept: 'application/json'
      }
    })
    this.axios.interceptors.request.use(this.interceptRequest)
    this.axios.interceptors.response.use(null, this.interceptErrorResponse)
  }

  createPromotion (data) {
    return this.axios.post('/promotions', data)
  }

  updatePromotion (data) {
    data.append('_method', 'PUT')
    return this.axios.post('/promotions', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  getPromotions () {
    return this.axios.get('/promotions')
  }

  getPromotionDetails (id) {
    return this.axios.get(`/promotions/${id}/details`)
  }

  createPromotionEvent (id, data) {
    return this.axios.post(`/promotions/${id}/events`, data)
  }

  getPaymentSources () {
    return this.axios.get('/payment-sources')
  }

  addPaymentSource (data) {
    return this.axios.post('/payment-sources', data)
  }

  buyLicense (data) {
    return this.axios.post('/licenses', data)
  }

  setFreeLicense () {
    return this.axios.post('/licenses/free')
  }

  getLicense () {
    return this.axios.get('/licenses')
  }

  cancelLicense () {
    return this.axios.post('/licenses/cancel')
  }

  activateLicense () {
    return this.axios.post('/licenses/activate')
  }

  updateSeats (license, data) {
    return this.axios.post(`/licenses/${license}/seats`, data)
  }

  getSharedSeats (license) {
    return this.axios.get(`/licenses/${license}/shared-seats`)
  }

  removeSharedSeats (license, data) {
    return this.axios.delete(`/licenses/${license}/shared-seats`, data)
  }

  changeSharedSeatState (license, data) {
    return this.axios.put(`/licenses/${license}/shared-seats`, data)
  }

  addSharedSeats (license, data) {
    return this.axios.post(`/licenses/${license}/shared-seats`, data)
  }

  partialRegistration (data) {
    return this.axios.post('/auth/partial-registration', data)
  }

  finishAccountRegistration (data) {
    return this.axios.post('auth/finish-registration', data)
  }

  async interceptRequest (config) {
    if (store.getters.isAuthenticated) {
      config.headers.Authorization = `Bearer ${store.getters.authToken}`
    }
    return config
  }

  async interceptErrorResponse (error) {
    if (error.response?.status === 401) {
      await store.dispatch('signOut')
    } else {
      let errorMessage = ''
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response?.data?.errors)
        if (errors.length > 0 && errors[0].length > 0) {
          errorMessage = errors[0][0]
        }
      } else {
        errorMessage = error.response?.data?.message
      }
      await store.dispatch(
        'resolveApiError',
        errorMessage ?? 'Whoops. An error occurred'
      )

      EventBus.$emit('onApiRequestFailed')
    }
    throw error
  }

  async loginViaInvite (orderUUID, attendeeId) {
    const response = await this.axios.post('/auth/login-invite', {
      order_uuid : orderUUID,
      attendee_id: attendeeId
    })
    const user = new User().map(response.data.data)
    return { token: response.data.token, user }
  }

  async loginAsCollaborator (orderUUID, collaborator) {
    const response = await this.axios.post(`/auth/login/collaborator`, {
      order_uuid  : orderUUID,
      collaborator: collaborator
    })
    const user = new User().map(response.data.data)
    await store.commit('SET_AUTH_DATA', { token: response.data.token, user })
    return { user }
  }

  getPortals (params) {
    return this.axios.get(`/portals`, params)
  }

  addPortal (uuid, portal_id) {
    return this.axios.post(`/orders/${uuid}/portals`, {
      'portal_id': portal_id
    })
  }

  getMyOrders (params) {
    return this.axios.get(`/orders`, params)
  }

  getOrderDetails (uuid) {
    return this.axios.get(`/orders/${uuid}`)
  }

  getOrderPrice (uuid) {
    return this.axios.get(`/orders/${uuid}/get-order-price`)
  }

  updateOrderAdjustedValue (uuid, data) {
    return this.axios.post(`/orders/${uuid}/adjusted-value`, data)
  }

  updateOrderDetails (uuid, order) {
    return this.axios.put(`/orders/${uuid}`, order)
  }

  updateOrderStatus (uuid, order) {
    return this.axios.put(`/orders/${uuid}/status`, order)
  }

  updateOrderDetailsFormdata (uuid, order) {
    return this.axios.post(`/orders/${uuid}`, order)
  }

  claimOrderOwner (uuid) {
    return this.axios.post(`/orders/${uuid}/owner`)
  }

  getOrderEnrollmentCategories (uuid, userid) {
    return this.axios.get(`/orders/${uuid}/enrollment-categories/${userid}`)
  }

  runOrderDemo (uuid) {
    return this.axios.post(`/orders/${uuid}/demo`)
  }

  deleteOrder (uuid) {
    return this.axios.delete(`/orders/${uuid}`)
  }

  changeOrderActiveState (orderUuid, state) {
    return this.axios.post(`/orders/${orderUuid}/change-active-state`, {
      active: state
    })
  }

  getCategoriesForOrder (uuid, type = ORDER_MENU_TYPE.CUSTOM) {
    return new Promise((resolve, reject) => {
      this.axios
          .get(`/orders/${uuid}/categories`, {
            params: {
              type: type
            }
          })
          .then(response => {
            const categories = response.data.map(category => {
              category.isSelected = false
              category.file = ''
              category.items = category.items.map(item => {
                item.isSelected = false
                item.file = ''
                return item
              })
              return category
            })
            resolve(categories)
          })
          .catch(error => {
            reject(error)
          })
    })
  }

  saveCategoryForOrder (uuid, category) {
    const formData = new FormData()
    formData.append('title', category.title)
    formData.append('name', category.name)
    formData.append('description', category.description)
    formData.append('items_quantity', category.items_quantity)
    formData.append('position', category.position)
    formData.append(
      'visible_on_enrollment_page',
      category.visible_on_enrollment_page
    )
    if (category.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', category.file)
    } else {
      formData.append('image', category.image)
    }
    if (category.uuid) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/orders/${uuid}/categories/${category.uuid}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(`/orders/${uuid}/categories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  }

  deleteCategoriesFromOrder (orderUUID, categoryIDs) {
    return this.axios.delete(`/orders/${orderUUID}/categories`, {
      data: {
        uuid: categoryIDs
      }
    })
  }

  sortCategoriesForOrder (orderUUID, categoryIDs) {
    return this.axios.post(`/orders/${orderUUID}/categories/sort`, {
      categories: categoryIDs
    })
  }

  getLineItemsForOder (orderUUID, categoryUUID) {
    return this.axios.get(
      `/orders/${orderUUID}/categories/${categoryUUID}/items`
    )
  }

  saveLineItemForOrder (uuid, categoryUUID, lineItem) {
    const formData = new FormData()
    formData.append('name', lineItem.name)
    formData.append('description', lineItem.description)
    formData.append('category_uuid', lineItem.category_uuid)
    formData.append('price', lineItem.price)
    formData.append('position', lineItem.position)
    if (lineItem.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', lineItem.file)
    } else {
      formData.append('image', lineItem.image)
    }
    if (lineItem.uuid) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/orders/${uuid}/categories/${categoryUUID}/items/${lineItem.uuid}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(
        `/orders/${uuid}/categories/${lineItem.category_uuid}/items`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    }
  }

  deleteLineItemsFromOrder (orderUUID, categoryUUID, itemIDs) {
    return this.axios.delete(
      `/orders/${orderUUID}/categories/${categoryUUID}/items`,
      {
        data: {
          uuid: itemIDs
        }
      }
    )
  }

  sortLineItemsForOrder (orderUUID, categoryUUID, itemIDs) {
    return this.axios.post(
      `/orders/${orderUUID}/categories/${categoryUUID}/items/sort`,
      {
        items: itemIDs
      }
    )
  }

  getMenuChoicesForOrder (orderUUID) {
    return this.axios.get(`/orders/${orderUUID}/menus`)
  }

  saveMenuChoicesForOrder (orderUUID, menus) {
    return this.axios.post(`/orders/${orderUUID}/menus`, {
      menus: menus
    })
  }

  getCharactersForOrder (uuid, selectedOnly = false) {
    return new Promise((resolve, reject) => {
      this.axios
          .get(`/orders/${uuid}/characters`)
          .then(response => {
            const characters = response.data
                                       .map(character => {
                                         character.file = ''
                                         return character
                                       })
                                       .filter(character => !selectedOnly || !!character.is_selected)
            resolve(characters)
          })
          .catch(error => {
            reject(error)
          })
    })
  }

  saveCharacterForOrder (uuid, character) {
    const formData = new FormData()
    formData.append('name', character.name)
    formData.append('description', character.description)
    formData.append('disclaimer', character.disclaimer)
    if (character.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', character.file)
    } else {
      formData.append('image', character.image)
    }
    if (character.id) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/orders/${uuid}/characters/${character.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(`/orders/${uuid}/characters`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  }

  saveCharacterChoicesForOrder (uuid, characters) {
    return this.axios.post(`/orders/${uuid}/characters/save`, {
      characters: characters
    })
  }

  deleteCharacterFromOrder (uuid, character) {
    return this.axios.delete(`/orders/${uuid}/characters/${character.id}`)
  }

  getMessagesOfOrder (uuid) {
    return this.axios.get(`/orders/${uuid}/messages`)
  }

  getMessageDetails (uuid, messageId) {
    return this.axios.get(`/orders/${uuid}/messages/${messageId}`)
  }

  saveMessageForOrder (uuid, message) {
    if (message.id) {
      return this.axios.put(`/orders/${uuid}/messages/${message.id}`, message)
    } else {
      return this.axios.post(`/orders/${uuid}/messages`, message)
    }
  }

  deleteMessageFromOrder (uuid, message) {
    return this.axios.delete(`/orders/${uuid}/messages/${message.id}`)
  }

  getAttendeesForOrder (uuid, params) {
    return this.axios.get(`/orders/${uuid}/attendees`, params)
  }

  getAttendeeDetailsOfOrder (orderUUID, attendeeID) {
    return this.axios.get(`/orders/${orderUUID}/attendees/${attendeeID}`)
  }

  addAttendeeToOrder (uuid, attendee) {
    return this.axios.post(`/orders/${uuid}/attendees`, attendee)
  }

  enrollAttendeeToOrder (uuid, attendee) {
    return this.axios.post(`/orders/${uuid}/attendee-enrollment`, attendee)
  }

  addMultipleAttendeesToOrder (uuid, attendees) {
    return this.axios.post(`/orders/${uuid}/attendees/upload/mass`, {
      attendees: attendees
    })
  }

  deleteAttendeeFromOrder (uuid, attendee) {
    return this.axios.delete(`/orders/${uuid}/attendees/${attendee.id}`)
  }

  uploadAttendeesToOrder (uuid, file) {
    const formData = new FormData()
    formData.append('file', file)
    return this.axios.post(`/orders/${uuid}/attendees/upload/csv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  sendInvitesToAttendeesOfOrder (uuid) {
    return this.axios.post(`/orders/${uuid}/attendees/invite`)
  }

  getCollaboratorsOfOrder (uuid) {
    return this.axios.get(`/orders/${uuid}/collaborators`)
  }

  getPortalsOfOrder (uuid) {
    return this.axios.get(`/orders/${uuid}/portals`)
  }

  generatePaymentIntentOfOrder (uuid, data) {
    return this.axios.post(`/orders/${uuid}/generate-payment-intent`, data)
  }

  setupIntent (uuid) {
    return this.axios.post(`/orders/${uuid}/setup-payment-intent`)
  }

  getPaymentMethods (uuid) {
    return this.axios.post(`/orders/${uuid}/get-payment-methods`)
  }

  checkPaymentIntentOfOrder (uuid, data) {
    return this.axios.post(`/orders/${uuid}/check-payment-intent`, data)
  }

  addCollaboratorToOrder (uuid, collaborator) {
    return this.axios.post(`/orders/${uuid}/collaborators`, collaborator)
  }

  removeCollaboratorFromOrder (uuid, collaborator) {
    return this.axios.delete(
      `/orders/${uuid}/collaborators/${collaborator.id}`,
      {
        params: {
          role: collaborator.role
        }
      }
    )
  }

  removePortalFromOrder (uuid, collaborator) {
    return this.axios.delete(
      `/orders/${uuid}/portals/?portal_id=${collaborator.id}`
    )
  }

  sendInvitesToCollaboratorsOfOrder (uuid) {
    return this.axios.post(`/orders/${uuid}/collaborators/invite`)
  }

  getOrderPriceDetails (uuid) {
    return this.axios.get(`/orders/${uuid}/price`)
  }

  applyPromoCodeToOrder (uuid, promoCode) {
    return this.axios.post(`/orders/${uuid}/promo`, {
      promo: promoCode
    })
  }

  getOrderPromoCode (uuid) {
    return this.axios.get(`/orders/${uuid}/promo`)
  }

  savePaymentTokenToOrder (uuid, token) {
    return this.axios.post(`/orders/${uuid}/checkout`, {
      token    : token.id,
      last_four: token.card.last4
    })
  }

  printOrderDetails (uuid) {
    return this.axios.get(`/orders/${uuid}/print`)
  }

  copyOrderTemplate (uuid) {
    return this.axios.post(`/orders/${uuid}/copy`)
  }

  getMyEvents () {
    return this.axios.get(`/events`)
  }

  getEventDetails (uuid) {
    return this.axios.get(`/events/${uuid}`)
  }

  // Sub-Order Attendee Process

  getSubOrder (orderUUID) {
    return this.axios.get(`/orders/${orderUUID}/suborders`)
  }

  createSubOrder (orderUUID, data) {
    return this.axios.post(`/orders/${orderUUID}/suborders`, data)
  }

  cancelSubOrder (orderUUID, data) {
    return this.axios.post(`/orders/${orderUUID}/suborders/cancel`, data)
  }

  updateSubOrder (orderUUID, subOrderID, data) {
    return this.axios.put(`/orders/${orderUUID}/suborders/${subOrderID}`, data)
  }

  saveSubOrderItems (orderUUID, subOrderID, categoryId, categoryType, items) {
    return this.axios.put(
      `/orders/${orderUUID}/suborders/${subOrderID}/items`,
      {
        category_id  : categoryId,
        category_type: categoryType,
        items        : items
      }
    )
  }

  saveSubOrderCharacter (orderUUID, subOrderID, characterId) {
    return this.axios.put(
      `/orders/${orderUUID}/suborders/${subOrderID}/character`,
      {
        character_id: characterId
      }
    )
  }

  loadSubOrderCategories (orderUUID, subOrderID) {
    return this.axios.get(
      `/orders/${orderUUID}/suborders/${subOrderID}/categories`
    )
  }

  getOrderManualSelectedItems (orderUUID) {
    return this.axios.get(`/orders/${orderUUID}/manual-selected-items`)
  }

  saveOrderManualSelectedItems (orderUUID, SubOrderID, data) {
    return this.axios.post(`/orders/${orderUUID}/suborders/${SubOrderID}/manual-selected-items`, data)
  }

  // Caterer
  storeCatererInformation (data) {
    return this.axios.post('/caterers/', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  linkCatererToUser (caterer_id, data) {
    return this.axios.post(`/caterers/${caterer_id}/assign-to-user`, data)
  }

  getMyBusinessCatererDetails (uuid) {
    return this.axios.get(`/caterers/${uuid}/mybusiness/`)
  }

  getUserCaterers () {
    return this.axios.get(`/caterers/getusercompanies/`)
  }

  getCatererDetails (caterer_id) {
    return this.axios.get(`/caterers/${caterer_id}/`)
  }

  getCatererCategories (caterer_id) {
    return this.axios.get(`/caterers/${caterer_id}/categories`)
  }

  getCatererMenu (caterer_id) {
    return this.axios.get(`/caterers/${caterer_id}/menu`)
  }

  saveCatererCategory (caterer_id, category) {
    const formData = new FormData()
    formData.append('name', category.name)
    formData.append('description', category.description)
    formData.append('items_quantity', category.items_quantity)
    formData.append('position', category.position)
    if (category.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', category.file)
    } else {
      formData.append('image', category.image)
    }
    if (category.id) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/caterers/${caterer_id}/categories/${category.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(`/caterers/${caterer_id}/categories`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  }

  deleteCatererCategories (caterer_id, category_ids) {
    return this.axios.delete(`/caterers/${caterer_id}/categories`, {
      data: {
        ids: category_ids
      }
    })
  }

  sortCatererCategories (caterer_id, category_ids) {
    return this.axios.post(`/caterers/${caterer_id}/categories/sort`, {
      ids: category_ids
    })
  }

  getCatererCategoryItems (caterer_id, category_id) {
    return this.axios.get(
      `/caterers/${caterer_id}/categories/${category_id}/items`
    )
  }

  saveCatererCategoryItem (caterer_id, category_id, lineItem) {
    const formData = new FormData()
    formData.append('name', lineItem.name)
    formData.append('description', lineItem.description)
    formData.append('category_id', lineItem.category_id)
    formData.append('price', lineItem.price)
    formData.append('position', lineItem.position)
    if (lineItem.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', lineItem.file)
    } else {
      formData.append('image', lineItem.image)
    }
    if (lineItem.id) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/caterers/${caterer_id}/categories/${category_id}/items/${lineItem.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(
        `/caterers/${caterer_id}/categories/${category_id}/items`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    }
  }

  deleteCatererCategoryItems (caterer_id, category_id, item_ids) {
    return this.axios.delete(
      `/caterers/${caterer_id}/categories/${category_id}/items`,
      {
        data: {
          ids: item_ids
        }
      }
    )
  }

  sortCatererCategoryItems (caterer_id, category_id, item_ids) {
    return this.axios.post(
      `/caterers/${caterer_id}/categories/${category_id}/items/sort`,
      {
        ids: item_ids
      }
    )
  }

  getCaterersByLocation (index) {
    return this.axios.get(`/caterers/locations`, {
      params: {
        index
      }
    })
  }

  getCaterers (data) {
    return this.axios.get(`/caterers`, data)
  }

  getCatererAvailableStates (data) {
    return this.axios.get(`/caterers/states`, data)
  }

  getCuisineTypes () {
    return this.axios.get('cuisine-types')
  }

  getAvailableCuisineTypes () {
    return this.axios.get('cuisine-types/available')
  }

  setOrderCaterer (order_uuid, caterer_id) {
    return this.axios.post(`/orders/${order_uuid}/set-caterer/${caterer_id}`)
  }

  createOrAddOrderCollaborator (order_uuid, data) {
    return this.axios.post(
      `/orders/${order_uuid}/find-or-create-collaborator`,
      { collaborators: data }
    )
  }

  loginAsCaterer (data) {
    return this.axios.post('/caterers/login-from-invite', data)
  }

  getLocationsPrice (data) {
    return this.axios.post('/caterers/get-location-price', data)
  }

  catererCheckoutLocations (caterer_id, data) {
    return this.axios.post(`/caterers/${caterer_id}/checkout`, data)
  }

  removeCatererLocation (caterer_id, location) {
    return this.axios.delete(
      `/caterers/${caterer_id}/remove-location/${location}`
    )
  }

  getCatererEvents (uuid) {
    return this.axios.get(`/caterers/${uuid}/events`)
  }

  createOrderWithCaterer (uuid, from = 'buildabox') {
    return this.axios.post(`/caterers/${uuid}/create-order/?from=${from}`)
  }

  createBuildaboxOrder () {
    return this.axios.post('create-buildabox-order')
  }

  getCatererBySlug (slug) {
    return this.axios.get(`/caterers/by-slug/${slug}`)
  }

  // Event planners
  getEventPlannerServices () {
    return this.axios.get('/event-planners/services')
  }

  storeEventPlanner (data) {
    return this.axios.post('/event-planners', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  getMyEventPlannerCompanies () {
    return this.axios.get(`/event-planners/getusercompanies`)
  }

  getMyBusinessEventPlannerDetails (uuid) {
    return this.axios.get(`/event-planners/${uuid}/my-business`)
  }

  getEventPlannerCategories (event_planner_id) {
    return this.axios.get(`/event-planners/${event_planner_id}/categories`)
  }

  saveEventPlannerCategory (event_planner_id, category) {
    const formData = new FormData()
    formData.append('name', category.name)
    formData.append('description', category.description)
    formData.append('items_quantity', category.items_quantity)
    formData.append('position', category.position)
    if (category.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', category.file)
    } else {
      formData.append('image', category.image)
    }
    if (category.id) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/event-planners/${event_planner_id}/categories/${category.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(
        `/event-planners/${event_planner_id}/categories`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    }
  }

  deleteEventPlannerCategories (event_planner_id, category_ids) {
    return this.axios.delete(`/event-planners/${event_planner_id}/categories`, {
      data: {
        ids: category_ids
      }
    })
  }

  sortEventPlannerCategories (event_planner_id, category_ids) {
    return this.axios.post(
      `/event-planners/${event_planner_id}/categories/sort`,
      {
        ids: category_ids
      }
    )
  }

  saveEventPlannerCategoryItem (event_planner_id, category_id, lineItem) {
    const formData = new FormData()
    formData.append('name', lineItem.name)
    formData.append('description', lineItem.description)
    formData.append('category_id', lineItem.category_id)
    formData.append('price', lineItem.price)
    formData.append('position', lineItem.position)
    if (lineItem.file) {
      // When there is a new image file to be uploaded, exclude the image url in the request.
      formData.append('file', lineItem.file)
    } else {
      formData.append('image', lineItem.image)
    }
    if (lineItem.id) {
      formData.append('_method', 'PUT')
      return this.axios.post(
        `/event-planners/${event_planner_id}/categories/${category_id}/items/${lineItem.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    } else {
      return this.axios.post(
        `/event-planners/${event_planner_id}/categories/${category_id}/items`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
    }
  }

  sortEventPlannerCategoryItems (event_planner_id, category_id, item_ids) {
    return this.axios.post(
      `/event-planners/${event_planner_id}/categories/${category_id}/items/sort`,
      {
        ids: item_ids
      }
    )
  }

  deleteEventPlannerCategoryItems (event_planner_id, category_id, item_ids) {
    return this.axios.delete(
      `/event-planners/${event_planner_id}/categories/${category_id}/items`,
      {
        data: {
          ids: item_ids
        }
      }
    )
  }

  getEventPlannerLocationsPrice (data) {
    return this.axios.post('/event-planners/get-location-price', data)
  }

  removeEventPlannerLocation (event_planner_id, location) {
    return this.axios.delete(
      `/event-planners/${event_planner_id}/remove-location/${location}`
    )
  }

  linkEPToUser (event_planner_id) {
    return this.axios.post(
      `/event-planners/${event_planner_id}/assign-to-user`
    )
  }

  eventPlannerCheckoutLocations (event_planner_id, data) {
    return this.axios.post(
      `/event-planners/${event_planner_id}/checkout`,
      data
    )
  }

  getEventPlannerEvents (event_planner_id) {
    return this.axios.get(`/event-planners/${event_planner_id}/events`)
  }

  getSegmentationQuestion (question_id) {
    return this.axios.get(`/segmentations/questions/${question_id}`)
  }

  getEventPlannersByLocation (location) {
    return this.axios.get(`/event-planners/get-by-location/${location}`)
  }

  setOrderEventPlanner (order_uuid, ep_id) {
    return this.axios.post(`/orders/${order_uuid}/set-event-planner/${ep_id}`)
  }

  getSegmentationQuestions () {
    return this.axios.get('/segmentations/questions')
  }

  storeSegmentationCsrFormData (data) {
    return this.axios.post('/segmentations/csrform', data)
  }

  loginAsEventPlanner (data) {
    return this.axios.post('/event-planners/login-from-invite', data)
  }

  createOrderWithEvplanner (uuid) {
    return this.axios.post(`/event-planners/${uuid}/create-order`)
  }

  getEvplannerBySlug (slug) {
    return this.axios.get(`/event-planners/by-slug/${slug}`)
  }

  getOrderSFtoken (uuid) {
    return this.axios.get(`/orders/${uuid}/salesforce-token`)
  }

  setOrderSFtokenOptions (uuid, data) {
    return this.axios.put(`/orders/${uuid}/order-sftoken-options`, data)
  }

  getOrderExportedAttendees (uuid) {
    return this.axios.get(`/orders/${uuid}/exported-attendees`)
  }

  getDefaultPaymentSource () {
    return this.axios.get(`/payment-sources/default`)
  }

  setDefaultPaymentSource (sourceId) {
    return this.axios.post(`/payment-sources/${sourceId}/default`)
  }

  getUserInvoices () {
    return this.axios.get('/invoices')
  }
}

export default new APIClient()
